import { faFileAlt } from "@fortawesome/free-regular-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSliders } from "@fortawesome/free-solid-svg-icons";
import { Button, FormControl, FormLabel, FormSelect, InputGroup, Tab, Tabs } from "react-bootstrap"
import InputGroupText from "react-bootstrap/esm/InputGroupText";
import { IFieldEditor } from "../../../../models/Interfaces";
import React, { useEffect, useMemo, useState } from "react";
import AppModal from "../../../components/common/AppModal";

type BootstrapHelperState = {
  paddingY: string;
  paddingX: string;
  marginBottom: string;
  display: string;
  justify: string;
  align: string;
  textAlign: string;
  textStyle: string;
  headingStyle: string;
  displayStyle: string;
  fontWeight: string;
  fontStyle: string;
  textTransform: string;
  textWrap: string;
  textDecoration: string;
  textColour: string;
  backgroundColour: string;
  border: string;
  borderWidth: string;
  borderColour: string;
  corners: string;
  shadow: string;
  buttonVariant: string;
  buttonSize: string;
};

const colourOptions = [
  { value: "", label: "Default" },
  { value: "primary", label: "Primary" },
  { value: "secondary", label: "Secondary" },
  { value: "success", label: "Success" },
  { value: "danger", label: "Danger" },
  { value: "warning", label: "Warning" },
  { value: "info", label: "Info" },
  { value: "light", label: "Light" },
  { value: "dark", label: "Dark" },
  { value: "white", label: "White" },
  { value: "body", label: "Body" },
  { value: "transparent", label: "Transparent" },
];

const colourSwatchMap: Record<string, string> = {
  primary: "#0d6efd",
  secondary: "#6c757d",
  success: "#198754",
  danger: "#dc3545",
  warning: "#ffc107",
  info: "#0dcaf0",
  light: "#f8f9fa",
  dark: "#212529",
  white: "#ffffff",
  body: "#212529",
  transparent: "transparent",
};

const ColourSelect = ({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (value: string) => void;
  options: Array<{ value: string; label: string }>;
}) => {
  const selectedOption = options.find((option) => option.value === value) ?? options[0];
  const swatchValue = selectedOption?.value ? (colourSwatchMap[selectedOption.value] ?? "#ced4da") : "#ffffff";

  return (
    <div>
      <FormLabel className="small mb-1">{label}</FormLabel>
      <InputGroup size="sm">
        <InputGroupText title={selectedOption?.label ?? "Default"}>
          <span
            style={{
              display: "inline-block",
              width: "0.85rem",
              height: "0.85rem",
              borderRadius: "999px",
              border: "1px solid rgba(15, 23, 42, 0.16)",
              backgroundColor: swatchValue,
            }}
          />
        </InputGroupText>
        <FormSelect size="sm" value={value} onChange={(e) => onChange(e.target.value)}>
          {options.map((option) => (
            <option key={`colour-option-${option.value || "default"}`} value={option.value}>
              {option.label}
            </option>
          ))}
        </FormSelect>
      </InputGroup>
    </div>
  );
};

const helperDefaults: BootstrapHelperState = {
  paddingY: "",
  paddingX: "",
  marginBottom: "",
  display: "",
  justify: "",
  align: "",
  textAlign: "",
  textStyle: "",
  headingStyle: "",
  displayStyle: "",
  fontWeight: "",
  fontStyle: "",
  textTransform: "",
  textWrap: "",
  textDecoration: "",
  textColour: "",
  backgroundColour: "",
  border: "",
  borderWidth: "",
  borderColour: "",
  corners: "",
  shadow: "",
  buttonVariant: "",
  buttonSize: "",
};

const helperFamilies = [
  /^py-\d$/,
  /^px-\d$/,
  /^mb-(?:\d|auto)$/,
  /^d-(?:block|inline-block|flex|grid|none)$/,
  /^justify-content-(?:start|center|end|between|around|evenly)$/,
  /^align-items-(?:start|center|end|stretch)$/,
  /^text-(?:start|center|end)$/,
  /^lead$/,
  /^small$/,
  /^h[1-6]$/,
  /^display-[1-6]$/,
  /^text-(?:lowercase|uppercase|capitalize)$/,
  /^text-(?:primary|secondary|success|danger|warning|info|light|dark|white|body)$/,
  /^text-(?:wrap|nowrap|break)$/,
  /^text-decoration-(?:underline|line-through|none)$/,
  /^bg-(?:primary|secondary|success|danger|warning|info|light|dark|white|transparent)$/,
  /^border$/,
  /^border-[1-5]$/,
  /^border-(?:primary|secondary|success|danger|warning|info|light|dark|white)$/,
  /^fw-(?:light|normal|semibold|bold)$/,
  /^fst-(?:italic)$/,
  /^rounded(?:-pill)?$/,
  /^shadow(?:-sm|-lg)?$/,
  /^btn-(?:primary|secondary|success|danger|warning|info|light|dark|link|outline-primary|outline-secondary|outline-success|outline-danger|outline-warning|outline-info|outline-light|outline-dark)$/,
  /^btn-(?:sm|lg)$/,
];

const buildManagedClasses = (state: BootstrapHelperState): string[] => {
  return [
    state.paddingY ? `py-${state.paddingY}` : "",
    state.paddingX ? `px-${state.paddingX}` : "",
    state.marginBottom ? `mb-${state.marginBottom}` : "",
    state.display ? `d-${state.display}` : "",
    state.justify ? `justify-content-${state.justify}` : "",
    state.align ? `align-items-${state.align}` : "",
    state.textAlign ? `text-${state.textAlign}` : "",
    state.textStyle,
    state.headingStyle,
    state.displayStyle,
    state.fontWeight ? `fw-${state.fontWeight}` : "",
    state.fontStyle ? `fst-${state.fontStyle}` : "",
    state.textTransform ? `text-${state.textTransform}` : "",
    state.textWrap ? `text-${state.textWrap}` : "",
    state.textDecoration ? `text-decoration-${state.textDecoration}` : "",
    state.textColour ? `text-${state.textColour}` : "",
    state.backgroundColour ? `bg-${state.backgroundColour}` : "",
    state.border,
    state.border && state.borderWidth ? `border-${state.borderWidth}` : "",
    state.border && state.borderColour ? `border-${state.borderColour}` : "",
    state.corners,
    state.shadow,
    state.buttonVariant ? `btn-${state.buttonVariant}` : "",
    state.buttonSize ? `btn-${state.buttonSize}` : "",
  ].filter((value) => value.length > 0);
};

const extractCustomClasses = (value: string): string[] => {
  return value
    .split(/\s+/)
    .map((part) => part.trim())
    .filter((part) => part.length > 0)
    .filter((part) => !helperFamilies.some((pattern) => pattern.test(part)));
};

const parseHelperState = (value: string): BootstrapHelperState => {
  const parts = value.split(/\s+/).map((part) => part.trim()).filter(Boolean);
  const nextState = { ...helperDefaults };

  parts.forEach((part) => {
    if (part.startsWith("py-")) nextState.paddingY = part.replace("py-", "");
    else if (part.startsWith("px-")) nextState.paddingX = part.replace("px-", "");
    else if (part.startsWith("mb-")) nextState.marginBottom = part.replace("mb-", "");
    else if (part.startsWith("d-")) nextState.display = part.replace("d-", "");
    else if (part.startsWith("justify-content-")) nextState.justify = part.replace("justify-content-", "");
    else if (part.startsWith("align-items-")) nextState.align = part.replace("align-items-", "");
    else if (part === "text-start" || part === "text-center" || part === "text-end") nextState.textAlign = part.replace("text-", "");
    else if (part === "lead" || part === "small") nextState.textStyle = part;
    else if (/^h[1-6]$/.test(part)) nextState.headingStyle = part;
    else if (/^display-[1-6]$/.test(part)) nextState.displayStyle = part;
    else if (part === "text-lowercase" || part === "text-uppercase" || part === "text-capitalize") nextState.textTransform = part.replace("text-", "");
    else if (part === "text-wrap" || part === "text-nowrap" || part === "text-break") nextState.textWrap = part.replace("text-", "");
    else if (part === "text-decoration-underline" || part === "text-decoration-line-through" || part === "text-decoration-none") nextState.textDecoration = part.replace("text-decoration-", "");
    else if (part === "text-primary" || part === "text-secondary" || part === "text-success" || part === "text-danger" || part === "text-warning" || part === "text-info" || part === "text-light" || part === "text-dark" || part === "text-white" || part === "text-body") nextState.textColour = part.replace("text-", "");
    else if (part.startsWith("fw-")) nextState.fontWeight = part.replace("fw-", "");
    else if (part.startsWith("fst-")) nextState.fontStyle = part.replace("fst-", "");
    else if (part.startsWith("bg-")) nextState.backgroundColour = part.replace("bg-", "");
    else if (part === "border") nextState.border = part;
    else if (/^border-[1-5]$/.test(part)) nextState.borderWidth = part.replace("border-", "");
    else if (part.startsWith("border-")) nextState.borderColour = part.replace("border-", "");
    else if (part === "rounded" || part === "rounded-pill") nextState.corners = part;
    else if (part === "shadow" || part === "shadow-sm" || part === "shadow-lg") nextState.shadow = part;
    else if (part === "btn-sm" || part === "btn-lg") nextState.buttonSize = part.replace("btn-", "");
    else if (
      part === "btn-primary" ||
      part === "btn-secondary" ||
      part === "btn-success" ||
      part === "btn-danger" ||
      part === "btn-warning" ||
      part === "btn-info" ||
      part === "btn-light" ||
      part === "btn-dark" ||
      part === "btn-link" ||
      part === "btn-outline-primary" ||
      part === "btn-outline-secondary" ||
      part === "btn-outline-success" ||
      part === "btn-outline-danger" ||
      part === "btn-outline-warning" ||
      part === "btn-outline-info" ||
      part === "btn-outline-light" ||
      part === "btn-outline-dark"
    ) nextState.buttonVariant = part.replace("btn-", "");
  });
  return nextState;
};

const StringEditor = (props: IFieldEditor) => {
  const [showHelper, setShowHelper] = useState<boolean>(false);
  const [helperState, setHelperState] = useState<BootstrapHelperState>(helperDefaults);
  const [activeHelperTab, setActiveHelperTab] = useState<string>("spacing");

  const currentValue = props.property.PublishedValue || props.property.DefaultValue || "";
  const useFrameworkHelper = props.property.UseFrameworkHelper === true;

  const onInputChange = (event: React.ChangeEvent<HTMLInputElement>) : void => {
    const { value } = event.target;
    props.onValueChange(props.property.Id, value);
  }

  const helperPreview = useMemo(() => {
    const customClasses = extractCustomClasses(currentValue);
    const managedClasses = buildManagedClasses(helperState);
    return [...customClasses, ...managedClasses].join(" ").trim();
  }, [currentValue, helperState]);

  const applyHelperClasses = (): void => {
    props.onValueChange(props.property.Id, helperPreview);
    setShowHelper(false);
  };

  const clearHelperClasses = (): void => {
    setHelperState(helperDefaults);
  };

  useEffect(() => {
    if (showHelper) {
      setHelperState(parseHelperState(currentValue));
    }
  }, [showHelper, currentValue]);

  useEffect(() => {
    props.onEditorOpen?.(showHelper);

    return () => {
      props.onEditorOpen?.(false);
    };
  }, [showHelper, props.onEditorOpen]);

  return (
    <>
      <FormLabel>{props.property.DisplayLabel}</FormLabel>
      <InputGroup>
        <InputGroupText>
          <FontAwesomeIcon icon={faFileAlt} />
        </InputGroupText>
        <FormControl
          type="text"
          value={currentValue} 
          title={`enter ${props.property.DisplayLabel} value`}
          onChange={onInputChange}
          placeholder={`${props.property.DisplayLabel?.toLowerCase() ?? "enter value"}...`} />
        {useFrameworkHelper && (
          <InputGroupText title="style options" style={{ cursor: "pointer" }}>
            <span onClick={() => setShowHelper(true)}>
              <FontAwesomeIcon icon={faSliders} />
            </span>
          </InputGroupText>
        )}
      </InputGroup>
      {useFrameworkHelper && showHelper && (
        <AppModal
          showButtons={false}
          size="lg"
          onClose={() => setShowHelper(false)}
          backdrop={false}
          modalClassName="framework-helper-modal shadow-lg"
          title="Style options">
          <div className="d-grid gap-3">
            <p className="small text-muted mb-0">
              Choose simple styling options and we will build the Bootstrap utility classes for you.
            </p>
                <Tabs
                  activeKey={activeHelperTab}
                  onSelect={(key) => setActiveHelperTab(key ?? "spacing")}
                  className="">
                  <Tab eventKey="spacing" title="Spacing">
                    <div>
                      <div className="row g-3">
                        <div className="col-md-4">
                          <FormLabel className="small mb-1">Space inside vertically</FormLabel>
                          <FormSelect size="sm" value={helperState.paddingY} onChange={(e) => setHelperState((prev) => ({ ...prev, paddingY: e.target.value }))}>
                            <option value="">None</option>
                            <option value="1">Small</option>
                            <option value="2">Medium</option>
                            <option value="3">Comfortable</option>
                            <option value="4">Large</option>
                            <option value="5">Extra large</option>
                          </FormSelect>
                        </div>
                        <div className="col-md-4">
                          <FormLabel className="small mb-1">Space inside horizontally</FormLabel>
                          <FormSelect size="sm" value={helperState.paddingX} onChange={(e) => setHelperState((prev) => ({ ...prev, paddingX: e.target.value }))}>
                            <option value="">None</option>
                            <option value="1">Small</option>
                            <option value="2">Medium</option>
                            <option value="3">Comfortable</option>
                            <option value="4">Large</option>
                            <option value="5">Extra large</option>
                          </FormSelect>
                        </div>
                        <div className="col-md-4">
                          <FormLabel className="small mb-1">Space below</FormLabel>
                          <FormSelect size="sm" value={helperState.marginBottom} onChange={(e) => setHelperState((prev) => ({ ...prev, marginBottom: e.target.value }))}>
                            <option value="">None</option>
                            <option value="1">Small</option>
                            <option value="2">Medium</option>
                            <option value="3">Comfortable</option>
                            <option value="4">Large</option>
                            <option value="5">Extra large</option>
                            <option value="auto">Auto</option>
                          </FormSelect>
                        </div>
                      </div>
                    </div>
                  </Tab>

                  <Tab eventKey="layout" title="Layout">
                    <div>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <FormLabel className="small mb-1">Content layout</FormLabel>
                          <FormSelect size="sm" value={helperState.display} onChange={(e) => setHelperState((prev) => ({ ...prev, display: e.target.value }))}>
                            <option value="">Normal</option>
                            <option value="block">Block</option>
                            <option value="inline-block">Inline block</option>
                            <option value="flex">Horizontal / flex</option>
                            <option value="grid">Grid</option>
                            <option value="none">Hide</option>
                          </FormSelect>
                        </div>
                        <div className="col-md-6">
                          <FormLabel className="small mb-1">Position content</FormLabel>
                          <FormSelect size="sm" value={helperState.justify} onChange={(e) => setHelperState((prev) => ({ ...prev, justify: e.target.value }))}>
                            <option value="">Default</option>
                            <option value="start">Start</option>
                            <option value="center">Centre</option>
                            <option value="end">End</option>
                            <option value="between">Spread out</option>
                            <option value="around">Space around</option>
                            <option value="evenly">Space evenly</option>
                          </FormSelect>
                        </div>
                        <div className="col-md-6">
                          <FormLabel className="small mb-1">Align items</FormLabel>
                          <FormSelect size="sm" value={helperState.align} onChange={(e) => setHelperState((prev) => ({ ...prev, align: e.target.value }))}>
                            <option value="">Default</option>
                            <option value="start">Top / start</option>
                            <option value="center">Centre</option>
                            <option value="end">Bottom / end</option>
                            <option value="stretch">Stretch</option>
                          </FormSelect>
                        </div>
                      </div>
                    </div>
                  </Tab>

                  <Tab eventKey="text" title="Text">
                    <div>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <FormLabel className="small mb-1">Text alignment</FormLabel>
                          <FormSelect size="sm" value={helperState.textAlign} onChange={(e) => setHelperState((prev) => ({ ...prev, textAlign: e.target.value }))}>
                            <option value="">Default</option>
                            <option value="start">Left</option>
                            <option value="center">Centre</option>
                            <option value="end">Right</option>
                          </FormSelect>
                        </div>
                        <div className="col-md-6">
                          <FormLabel className="small mb-1">Text weight</FormLabel>
                          <FormSelect size="sm" value={helperState.fontWeight} onChange={(e) => setHelperState((prev) => ({ ...prev, fontWeight: e.target.value }))}>
                            <option value="">Default</option>
                            <option value="light">Light</option>
                            <option value="normal">Normal</option>
                            <option value="semibold">Semibold</option>
                            <option value="bold">Bold</option>
                          </FormSelect>
                        </div>
                        <div className="col-md-6">
                          <FormLabel className="small mb-1">Text emphasis</FormLabel>
                          <FormSelect size="sm" value={helperState.fontStyle} onChange={(e) => setHelperState((prev) => ({ ...prev, fontStyle: e.target.value }))}>
                            <option value="">Normal</option>
                            <option value="italic">Italic</option>
                          </FormSelect>
                        </div>
                        <div className="col-md-6">
                          <FormLabel className="small mb-1">Text style</FormLabel>
                          <FormSelect size="sm" value={helperState.textStyle} onChange={(e) => setHelperState((prev) => ({ ...prev, textStyle: e.target.value }))}>
                            <option value="">Normal</option>
                            <option value="lead">Lead</option>
                            <option value="small">Small</option>
                          </FormSelect>
                        </div>
                        <div className="col-md-6">
                          <FormLabel className="small mb-1">Heading appearance</FormLabel>
                          <FormSelect size="sm" value={helperState.headingStyle} onChange={(e) => setHelperState((prev) => ({ ...prev, headingStyle: e.target.value, displayStyle: e.target.value ? "" : prev.displayStyle }))}>
                            <option value="">None</option>
                            <option value="h1">H1</option>
                            <option value="h2">H2</option>
                            <option value="h3">H3</option>
                            <option value="h4">H4</option>
                            <option value="h5">H5</option>
                            <option value="h6">H6</option>
                          </FormSelect>
                        </div>
                        <div className="col-md-6">
                          <FormLabel className="small mb-1">Display appearance</FormLabel>
                          <FormSelect size="sm" value={helperState.displayStyle} onChange={(e) => setHelperState((prev) => ({ ...prev, displayStyle: e.target.value, headingStyle: e.target.value ? "" : prev.headingStyle }))}>
                            <option value="">None</option>
                            <option value="display-1">Display 1</option>
                            <option value="display-2">Display 2</option>
                            <option value="display-3">Display 3</option>
                            <option value="display-4">Display 4</option>
                            <option value="display-5">Display 5</option>
                            <option value="display-6">Display 6</option>
                          </FormSelect>
                        </div>
                        <div className="col-md-6">
                          <FormLabel className="small mb-1">Text case</FormLabel>
                          <FormSelect size="sm" value={helperState.textTransform} onChange={(e) => setHelperState((prev) => ({ ...prev, textTransform: e.target.value }))}>
                            <option value="">Normal</option>
                            <option value="uppercase">Uppercase</option>
                            <option value="lowercase">Lowercase</option>
                            <option value="capitalize">Capitalize each word</option>
                          </FormSelect>
                        </div>
                        <div className="col-md-6">
                          <FormLabel className="small mb-1">Wrapping</FormLabel>
                          <FormSelect size="sm" value={helperState.textWrap} onChange={(e) => setHelperState((prev) => ({ ...prev, textWrap: e.target.value }))}>
                            <option value="">Normal</option>
                            <option value="nowrap">Keep on one line</option>
                            <option value="wrap">Allow wrapping</option>
                            <option value="break">Break long words</option>
                          </FormSelect>
                        </div>
                        <div className="col-md-6">
                          <FormLabel className="small mb-1">Decoration</FormLabel>
                          <FormSelect size="sm" value={helperState.textDecoration} onChange={(e) => setHelperState((prev) => ({ ...prev, textDecoration: e.target.value }))}>
                            <option value="">None</option>
                            <option value="underline">Underline</option>
                            <option value="line-through">Line through</option>
                          </FormSelect>
                        </div>
                      </div>
                    </div>
                  </Tab>

                  <Tab eventKey="colours" title="Colours">
                    <div>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <ColourSelect
                            label="Text colour"
                            value={helperState.textColour}
                            onChange={(value) => setHelperState((prev) => ({ ...prev, textColour: value }))}
                            options={colourOptions.filter(option => option.value !== "transparent")}
                          />
                        </div>
                        <div className="col-md-6">
                          <ColourSelect
                            label="Background colour"
                            value={helperState.backgroundColour}
                            onChange={(value) => setHelperState((prev) => ({ ...prev, backgroundColour: value }))}
                            options={colourOptions}
                          />
                        </div>
                        <div className="col-md-6">
                          <ColourSelect
                            label="Border colour"
                            value={helperState.borderColour}
                            onChange={(value) => setHelperState((prev) => ({ ...prev, borderColour: value, border: value ? "border" : prev.border }))}
                            options={colourOptions.filter(option => ["", "primary", "secondary", "success", "danger", "warning", "info", "light", "dark", "white"].includes(option.value))}
                          />
                        </div>
                      </div>
                    </div>
                  </Tab>

                  <Tab eventKey="effects" title="Effects">
                    <div>
                      <div className="row g-3">
                        <div className="col-md-6">
                          <FormLabel className="small mb-1">Border</FormLabel>
                          <FormSelect size="sm" value={helperState.border} onChange={(e) => setHelperState((prev) => ({ ...prev, border: e.target.value, borderWidth: e.target.value ? prev.borderWidth : "", borderColour: e.target.value ? prev.borderColour : "" }))}>
                            <option value="">None</option>
                            <option value="border">Show border</option>
                          </FormSelect>
                        </div>
                        <div className="col-md-6">
                          <FormLabel className="small mb-1">Border size</FormLabel>
                          <FormSelect size="sm" value={helperState.borderWidth} onChange={(e) => setHelperState((prev) => ({ ...prev, borderWidth: e.target.value, border: e.target.value ? "border" : prev.border }))}>
                            <option value="">Default</option>
                            <option value="1">Thin</option>
                            <option value="2">Medium</option>
                            <option value="3">Thick</option>
                            <option value="4">Extra thick</option>
                            <option value="5">Maximum</option>
                          </FormSelect>
                        </div>
                        <div className="col-md-6">
                          <FormLabel className="small mb-1">Corners</FormLabel>
                          <FormSelect size="sm" value={helperState.corners} onChange={(e) => setHelperState((prev) => ({ ...prev, corners: e.target.value }))}>
                            <option value="">Square</option>
                            <option value="rounded">Rounded</option>
                            <option value="rounded-pill">Pill</option>
                          </FormSelect>
                        </div>
                        <div className="col-md-4">
                          <FormLabel className="small mb-1">Shadow</FormLabel>
                          <FormSelect size="sm" value={helperState.shadow} onChange={(e) => setHelperState((prev) => ({ ...prev, shadow: e.target.value }))}>
                            <option value="">None</option>
                            <option value="shadow-sm">Small</option>
                            <option value="shadow">Medium</option>
                            <option value="shadow-lg">Large</option>
                          </FormSelect>
                        </div>
                      </div>
                    </div>
                  </Tab>

                  <Tab eventKey="buttons" title="Buttons">
                    <div>
                      <div className="row g-3">
                        <div className="col-md-8">
                          <FormLabel className="small mb-1">Button style</FormLabel>
                          <FormSelect size="sm" value={helperState.buttonVariant} onChange={(e) => setHelperState((prev) => ({ ...prev, buttonVariant: e.target.value }))}>
                            <option value="">None</option>
                            <option value="primary">Primary</option>
                            <option value="secondary">Secondary</option>
                            <option value="success">Success</option>
                            <option value="danger">Danger</option>
                            <option value="warning">Warning</option>
                            <option value="info">Info</option>
                            <option value="light">Light</option>
                            <option value="dark">Dark</option>
                            <option value="link">Link</option>
                            <option value="outline-primary">Outline primary</option>
                            <option value="outline-secondary">Outline secondary</option>
                            <option value="outline-success">Outline success</option>
                            <option value="outline-danger">Outline danger</option>
                            <option value="outline-warning">Outline warning</option>
                            <option value="outline-info">Outline info</option>
                            <option value="outline-light">Outline light</option>
                            <option value="outline-dark">Outline dark</option>
                          </FormSelect>
                        </div>
                        <div className="col-md-4">
                          <FormLabel className="small mb-1">Button size</FormLabel>
                          <FormSelect size="sm" value={helperState.buttonSize} onChange={(e) => setHelperState((prev) => ({ ...prev, buttonSize: e.target.value }))}>
                            <option value="">Default</option>
                            <option value="sm">Small</option>
                            <option value="lg">Large</option>
                          </FormSelect>
                        </div>
                      </div>
                    </div>
                  </Tab>
                </Tabs>

              <div className="border rounded-3 p-3 bg-light-subtle mt-3">
                <div className="small fw-semibold mb-1">Classes to apply</div>
                <code className="small d-block">{helperPreview || "No Bootstrap helper classes selected yet."}</code>
              </div>

              <div className="d-flex justify-content-between align-items-center mt-4">
                <Button variant="outline-secondary" size="sm" onClick={clearHelperClasses}>Reset helper options</Button>
                <div className="d-flex gap-2">
                  <Button variant="outline-primary" size="sm" onClick={() => setShowHelper(false)}>Close</Button>
                  <Button variant="primary" size="sm" onClick={applyHelperClasses}>Apply to class field</Button>
                </div>
              </div>
          </div>
        </AppModal>
      )}
    </>);
}

export default StringEditor;
